import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { registerErrorToaster } from "../api/axios";

/**
 * Tiny toast system: a context exposes `showToast(message, { type, duration })`
 * and a fixed-position stack renders any queued messages.
 *
 * Deliberately lightweight — no third-party dependency, no portal. If you need
 * richer affordances (action buttons, stacking animations) reach for a proper
 * library.
 */

const ToastContext = createContext(null);

const DEFAULT_DURATION = 2500;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message, options = {}) => {
      const id = ++idRef.current;
      const toast = {
        id,
        message,
        type: options.type || "info", // 'info' | 'success' | 'error'
        duration: options.duration ?? DEFAULT_DURATION,
      };
      setToasts((prev) => [...prev, toast]);

      if (toast.duration > 0) {
        setTimeout(() => dismiss(id), toast.duration);
      }
      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      showToast,
      success: (msg, opts) => showToast(msg, { ...opts, type: "success" }),
      error: (msg, opts) => showToast(msg, { ...opts, type: "error" }),
      info: (msg, opts) => showToast(msg, { ...opts, type: "info" }),
      dismiss,
    }),
    [showToast, dismiss]
  );

  // Give the axios interceptor a way to surface network / 5xx errors
  // without each caller wrapping every api.get() in its own try/catch.
  useEffect(() => {
    registerErrorToaster(showToast);
    return () => registerErrorToaster(null);
  }, [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}

function ToastContainer({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm"
    >
      {toasts.map((t) => (
        <button
          key={t.id}
          onClick={() => onDismiss(t.id)}
          // role="alert" on errors makes screen readers interrupt with the
          // message; info/success stay polite so they don't steal focus from
          // whatever the user is reading.
          role={t.type === "error" ? "alert" : undefined}
          className={`text-left px-5 py-3 rounded-lg shadow-lg text-sm text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 ${
            t.type === "success"
              ? "bg-emerald-600"
              : t.type === "error"
                ? "bg-red-600"
                : "bg-gray-800"
          }`}
        >
          {t.message}
        </button>
      ))}
    </div>
  );
}
